import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import type { ViewStyle } from 'react-native';
import * as GestureHandler from 'react-native-gesture-handler';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import TimetableRow from 'src/presentation/components/molecules/TimetableRow';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import clamp from 'src/core/utils/clamp';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import type { ApiTimetableStage } from 'src/core/models/api/shared/ApiTimetable';
import type { TimeTablePageItem } from 'src/presentation/navigation/types/NavigationTypes';

dayjs.extend(utc);
dayjs.extend(timezone);

const ScrollView = GestureHandler.ScrollView;

interface TimetableBlocksProps {
	timetable: ApiTimetableStage[];
	starttime: number;
	endtime: number;
	eventUuid: string;
	/** Shown as the navigation title of a session opened from a block. */
	dayTitle: string;
	onItemPress: (item: TimeTablePageItem, title: string) => void;
}

/**
 * The block schedule: a horizontal time axis with one row per stage. Shared by the timetable days and by a
 * program day shown as blocks (#416).
 */
function TimetableBlocks({ timetable, starttime, endtime, eventUuid, dayTitle, onItemPress }: TimetableBlocksProps) {
	const scrollX = useRef(new Animated.Value(0)).current;
	const indicatorOffset = useRef(new Animated.Value(0)).current;
	const footerHeight = useFooterHeight();
	// The timetable columns and the "now" line run the full height of the app, which the
	// centred column on web shares with the window.
	const { height: windowHeight } = useWindowDimensions();

	const sizedStyles = useMemo(
		(): { timeIndicator: ViewStyle; bgCol: ViewStyle } => ({
			timeIndicator: { height: windowHeight },
			bgCol: { height: windowHeight },
		}),
		[windowHeight],
	);

	const verticalScrollRef = useRef<React.ElementRef<typeof ScrollView>>(null);
	const didAutoScrollRef = useRef(false);

	// Index of the stage (row) that holds the earliest session.
	const earliestRowIndex = useMemo(() => {
		let index = -1;
		let earliest = Infinity;
		timetable.forEach((table, i) => {
			const stageStart = Math.min(...(table.items ?? []).map((item) => item.startdate ?? Infinity));
			if (stageStart < earliest) {
				earliest = stageStart;
				index = i;
			}
		});
		return index;
	}, [timetable]);

	useEffect(() => {
		// Allow the one-time auto-scroll to run again when a different day loads.
		didAutoScrollRef.current = false;
	}, [timetable]);

	// Animate the "now" indicator whenever the loaded day changes.
	useEffect(() => {
		const currentTime = Date.now() / 1000;
		const duration = endtime - starttime;
		const timePassed = clamp(0, currentTime - starttime, duration);

		Animated.timing(indicatorOffset, {
			toValue: timePassed / 10,
			duration: 250,
			useNativeDriver: true,
		}).start();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [starttime, endtime]);

	const getCurrentTime = () => dayjs().tz('Europe/Amsterdam').format(Constants.timeFormat);

	const calculateOffset = (startdate: number) => {
		if (!startdate) return 0;
		const timeInHours = startdate / 3600;
		return timeInHours - Math.floor(timeInHours);
	};

	const renderRows = () =>
		timetable.map((table, index) => (
			<View
				key={`${table.title}-${index}`}
				onLayout={(e) => {
					const y = e.nativeEvent.layout.y;
					if (index === earliestRowIndex && !didAutoScrollRef.current) {
						didAutoScrollRef.current = true;
						// Brief delay so the list paints at the top first, then animate to the first session.
						setTimeout(() => {
							verticalScrollRef.current?.scrollTo({ y, animated: true });
						}, 300);
					}
				}}
			>
				<TimetableRow
					color={table.color ?? Colors.black}
					dayTitle={dayTitle}
					enddate={endtime}
					eventUuid={eventUuid}
					items={table.items}
					offset={calculateOffset(table.items[0]?.startdate ?? 0)}
					scrollPos={scrollX}
					stageTitle={table.title}
					startdate={starttime}
					title={table.title}
					onItemPress={onItemPress}
				/>
			</View>
		));

	// The hour along the top of the schedule, one label per half hour, lined up with the background columns.
	const renderTimeAxis = () => {
		// Same zero point the blocks are placed against: the whole hour before the first session, shifted by the
		// one column of run-up the row leaves.
		const axisStart = starttime - (starttime % 3600);
		const columns = Math.ceil((endtime - starttime) / 3600) * 2 + 2;

		return (
			<View style={styles.timeAxis}>
				{Array.from({ length: columns }).map((_value, i) => (
					<Text key={i} style={[styles.timeAxisLabel, { left: 180 * (i + 1) }]}>
						{dayjs.unix(axisStart + i * 1800).format(Constants.timeFormat)}
					</Text>
				))}
			</View>
		);
	};

	const renderBg = () => {
		const hours = (Math.ceil((endtime - starttime) / 3600) + 1) * 2;
		return Array.from({ length: hours + 1 }).map((_value, i) => (
			<View
				key={i}
				style={[
					styles.bgCol,
					sizedStyles.bgCol,
					{
						backgroundColor: i % 2 === 0 ? Colors.greyBgLight : Colors.greyBgAlt,
						left: 180 * i,
					},
				]}
			/>
		));
	};

	const now = Date.now() / 1000;

	return (
		<Animated.ScrollView
			bounces={false}
			contentContainerStyle={styles.scrollContent}
			horizontal={true}
			scrollEventThrottle={1}
			showsHorizontalScrollIndicator={false}
			onScroll={Animated.event([{ nativeEvent: { contentOffset: { x: scrollX } } }], { useNativeDriver: true })}
		>
			{renderBg()}
			{starttime < now && endtime > now && (
				<View style={styles.indicatorContainer}>
					<Animated.View style={[styles.currentTimeBubble, { left: indicatorOffset }]}>
						<Text style={styles.currentTimeText}>{getCurrentTime()}</Text>
					</Animated.View>
					<Animated.View style={[styles.triangle, { left: indicatorOffset }]} />
					<Animated.View style={[styles.timeIndicator, sizedStyles.timeIndicator, { left: indicatorOffset }]} />
				</View>
			)}
			{/* Column, so the axis sits above the stages and stays put while
				they scroll vertically; it does travel along horizontally. */}
			<View>
				{renderTimeAxis()}
				<ScrollView contentContainerStyle={{ paddingBottom: footerHeight }} ref={verticalScrollRef}>
					{renderRows()}
				</ScrollView>
			</View>
		</Animated.ScrollView>
	);
}

export default TimetableBlocks;

const styles = StyleSheet.create({
	scrollContent: {
		backgroundColor: Colors.greyBgAlt,
	},
	indicatorContainer: {
		zIndex: 99,
		position: 'absolute',
		top: 0,
	},
	timeIndicator: {
		position: 'absolute',
		width: 2,
		backgroundColor: Colors.yellowAccent,
		marginTop: Dimens.margin40,
	},
	currentTimeBubble: {
		backgroundColor: Colors.yellowAccent,
		padding: Dimens.padding5,
		width: 58,
		alignItems: 'center',
		marginLeft: -29,
		position: 'absolute',
		top: 0,
	},
	currentTimeText: {
		fontSize: Dimens.fontSize14,
		fontFamily: Fonts.montserratBold,
	},
	triangle: {
		position: 'absolute',
		top: 28,
		marginLeft: -4,
		width: 0,
		height: 0,
		borderStyle: 'solid',
		borderLeftWidth: 5,
		borderRightWidth: 5,
		borderBottomWidth: 5,
		borderLeftColor: Colors.transparent,
		borderRightColor: Colors.transparent,
		borderBottomColor: Colors.yellowAccent,
		transform: [{ rotate: '180deg' }],
	},
	timeAxis: {
		height: 22,
		marginTop: Dimens.margin8,
	},
	timeAxisLabel: {
		position: 'absolute',
		// Half a label's width to the left, so the time sits on the column edge it marks rather than beside it.
		marginLeft: -22,
		width: 44,
		textAlign: 'center',
		fontSize: Dimens.fontSize12,
		fontFamily: Fonts.montserratRegular,
		color: Colors.greySecondaryText,
	},
	bgCol: {
		width: 180,
		position: 'absolute',
		bottom: 0,
	},
});
